import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Award, Sparkles, Leaf, X, ArrowRight, ShieldCheck, Check, User, Building2, Quote } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const CERT_COPY = {
  sk: {
    trigger: "Čo je certifikát SauberEko®",
    badge: "Certifikát SauberEko®",
    title: "Pečať výnimočnosti",
    lead: "…a prečo je zlatá pečať viac než len logo.",
    originTitle: "Ako vznikol SauberEko®",
    originParas: [
      "Značka SauberEko® sa nezrodila v kancelárii marketingových agentúr. Zrodila sa v teréne — pri odborníkovi, ktorý po dvadsiatich rokoch praxe po Európe videl príliš veľa prasknutých projektov, nesplnených sľubov a lacnej práce vydávanej za profesionalitu.",
    ],
    originQuote: "Prišlo mi ľúto investorov, ktorí si za miliónové rozpočty kupovali kvalitu a dostávali prácu vyhotovenú narýchlo, bez dokumentácie a bez kontroly.",
    originAfter: [
      "To bol moment. Vznikla myšlienka postaviť značku, ktorá bude znamenať niečo skutočné. Nie prázdny nálepkový nápis. Ale overenú kvalitu — kontrolovanú treťou stranou, zapísanú v registri, spätne dohľadateľnú v čase.",
      "Sa.Eko. = SauberEko. Nemecké slovo sauber (čistý), grécke eko (dom, prostredie). Čistota v každom detaile — od prvého úkonu až po vzťah s klientom.",
    ],
    missionTitle: "Naša misia",
    missionIntro: "Postaviť európsky štandard, ktorý:",
    missionBullets: [
      "Chráni investora pred falošnými prísľubmi",
      "Vyzdvihuje pravých profesionálov nad davom nespôsobilých",
      "Buduje komunitu, kde skúsení zdieľajú vedomosti s novými",
      "Vracia remeslu česť, ktorú v ére „rýchlo, lacno, hlavne hotovo” stratilo",
    ],
    missionClose: "Certifikát Sa.Eko. nie je papier. Je to verejný záväzok človeka alebo firmy voči etickému spôsobu práce.",
    closeLabel: "Zavrieť",
    prsIcon: "user",
    prsTitle: "Certifikát Sa.Eko.Prs. — Certifikovaní profesionáli",
    prsQuote: "Odborníci s dlhoročnou praxou, ktorých rukopis pozná Európa.",
    prsBody: [
      "Držitelia zlatého certifikátu Sa.Eko.Prs. sú majstri rôznych odvetví s dlhoročnou praxou — profesionáli, prípravári, potrubári, technológovia, kontrolóri kvality a mnohí ďalší odborníci, ktorých ruky a hlavy tvoria hodnotu v desiatkach krajín — od západnej Európy až po Blízky východ.",
      "Ich profil spája prax, precíznosť a osobnú česť. V teréne sú tichí, dôslední, sústredení na detail. V tíme sú empatickí učitelia — trpezlivo vedú absolventov, delia sa o skúsenosti, ktoré získavali roky.",
    ],
    prsCondTitle: "Prísne podmienky certifikácie:",
    prsConds: [
      "Minimálne 5 rokov overenej medzinárodnej praxe",
      "Aspoň 3 doložené projekty v náročnom priemyselnom prostredí",
      "Priama kontrola kvality výsledkov práce treťou stranou",
      "Osobná previerka — spôsob práce, prístup k mladším kolegom, mlčanlivosť voči investorovi",
      "Podpis pod SauberEko® Kódex — poctivosť, čistota, rešpekt",
    ],
    prsClose: "Ak vidíš v komunite meno so zlatým odznakom certifikátu Sa.Eko.Prs., hovoríš s odborníkom, ktorému môžeš dôverovať naslepo.",
    faIcon: "building",
    faTitle: "Certifikát Sa.Eko.Fa. — Certifikované firmy",
    faQuote: "Partneri, ktorí držia slovo.",
    faBody: [
      "Firmy s pečaťou Sa.Eko.Fa. sú tie, ktoré si do bodky plnia svoje záväzky — voči zákazníkovi, voči zamestnancom aj voči dodávateľom. Nesľubujú nesplniteľné termíny, neriešia problémy odsúvaním viny, netlačia cenu na úkor kvality.",
      "Ich technická aj procesná vybavenosť je na najvyššej úrovni. Investujú do ľudí, do vzdelávania, do vybavenia a do bezpečnosti — nie iba do faktúr. Pracujú s riadeným systémom kvality a spätne dohľadateľnou dokumentáciou.",
    ],
    faCondTitle: "Prísne podmienky certifikácie:",
    faConds: [
      "Interný audit SauberEko® priamo na prevádzke — kontrola súladu s 10 piliermi Kódexu Sa.Eko. (Audítny protokol)",
      "Doložené splnenie termínov na 3 posledných projektoch (referencie od klientov)",
      "Priateľské vzťahy s dodávateľmi — platia faktúry včas, komunikujú férovo",
      "Bezpečnosť práce na najvyššej európskej úrovni",
    ],
    faClose: "Ak sa v Európe hľadajú firmy, ktoré niečo znamenajú vo svojom odbore, mená s pečaťou Sa.Eko.Fa. sú medzi prvými, na koho sa myslí.",
    codeTitle: "SauberEko® Kódex — 5 tichých pravidiel",
    codeIntro: "Každý držiteľ certifikátu podpisuje Kódex. Nie je vytesaný do žuly, ale je vytesaný do vzťahu s tou pečaťou:",
    codeRules: [
      ["Poctivosť", "Ak neviem, poviem „neviem”. Ak sa mýlim, priznám. Ak zaplatili za kvalitu, dostanú kvalitu."],
      ["Čistota", "Čistá práca, čistá dokumentácia, čisté pracovisko, čistý účet."],
      ["Rešpekt", "Voči investorovi, voči kolegovi, voči materiálu, voči remeslu."],
      ["Zdieľanie vedomostí", "Dobrý majster nezomrie s tajomstvom. Naučí to ďalšieho."],
      ["Kontinuálne učenie", "Kým rastie technológia, rastie aj remeslo. Nikdy nie sme „hotoví”."],
    ],
    whyTitle: "Prečo to všetko?",
    whyParas: [
      "Trh so službami je preplnený sľubmi. SauberEko® certifikát je jediná záruka, že za menom stojí overená kvalita — kontrolovaná treťou stranou, zapísaná v registri, verejne dohľadateľná aj o desať rokov.",
      "Investor, ktorý vyhľadáva Sa.Eko.Prs. odborníka alebo Sa.Eko.Fa. firmu, získava poistku proti neprofesionalite. Profesionál alebo firma, ktorá si zaslúži pečať Sa.Eko., získava verejné uznanie svojej práce a zaradenie do siete odborníkov, ktorí si navzájom pomáhajú a odovzdávajú si zákazky.",
    ],
    whyEmphasis: ["Byť certifikovaný neznamená byť dokonalý.", "Znamená to byť zodpovedný."],
    cta: "Pridajte sa k výnimočným — získajte certifikát SauberEko®.",
    ctaBtn: "Získať certifikát",
  },

  en: {
    trigger: "About the SauberEko® certificate",
    badge: "SauberEko® Certificate",
    title: "A mark of excellence",
    lead: "…and why the golden seal is more than just a logo.",
    originTitle: "How SauberEko® was born",
    originParas: [
      "The SauberEko® brand was not born in a marketing agency's office. It was born in the field — with an expert who, after twenty years of practice across Europe, had seen too many failed projects, broken promises and cheap work passed off as professionalism.",
    ],
    originQuote: "I felt sorry for investors who paid million-euro budgets for quality and received work done in a rush, without documentation and without inspection.",
    originAfter: [
      "That was the moment. The idea was born to build a brand that would mean something real. Not an empty sticker. But verified quality — checked by a third party, recorded in a registry, traceable over time.",
      "Sa.Eko. = SauberEko. The German word sauber (clean), the Greek eko (home, environment). Cleanliness in every detail — from the first task to the relationship with the client.",
    ],
    missionTitle: "Our mission",
    missionIntro: "To build a European standard that:",
    missionBullets: [
      "Protects the investor from false promises",
      "Elevates true professionals above the crowd of the unqualified",
      "Builds a community where the experienced share knowledge with newcomers",
      "Restores to the craft the honour it lost in the era of \"fast, cheap, just get it done\"",
    ],
    missionClose: "A Sa.Eko. certificate is not a piece of paper. It is a public commitment by a person or company to an ethical way of working.",
    closeLabel: "Close",
    prsIcon: "user",
    prsTitle: "Sa.Eko.Prs. Certificate — Certified Professionals",
    prsQuote: "Experts with years of practice, whose signature is known across Europe.",
    prsBody: [
      "Holders of the golden Sa.Eko.Prs. certificate are masters of various trades with long experience — professionals, fitters, pipefitters, technologists, quality controllers and many other experts whose hands and minds create value in dozens of countries — from Western Europe to the Middle East.",
      "Their profile combines practice, precision and personal honour. In the field they are quiet, thorough, focused on detail. In a team they are empathetic teachers — patiently guiding graduates and sharing experience gained over years.",
    ],
    prsCondTitle: "Strict certification conditions:",
    prsConds: [
      "At least 5 years of verified international experience",
      "At least 3 documented projects in demanding industrial environments",
      "Direct third-party quality inspection of work results",
      "A personal review — way of working, approach to younger colleagues, confidentiality towards the investor",
      "Signature under the SauberEko® Code — honesty, cleanliness, respect",
    ],
    prsClose: "If you see a name with the golden Sa.Eko.Prs. badge in the community, you are talking to an expert you can trust blindly.",
    faIcon: "building",
    faTitle: "Sa.Eko.Fa. Certificate — Certified Companies",
    faQuote: "Partners who keep their word.",
    faBody: [
      "Companies bearing the Sa.Eko.Fa. seal are those that fulfil their commitments to the letter — towards the customer, employees and suppliers alike. They don't promise impossible deadlines, don't solve problems by shifting blame, don't push price at the expense of quality.",
      "Their technical and procedural capability is at the highest level. They invest in people, education, equipment and safety — not just in invoices. They work with a managed quality system and traceable documentation.",
    ],
    faCondTitle: "Strict certification conditions:",
    faConds: [
      "SauberEko® internal on-site audit — verification against the 10 pillars of the Sa.Eko. Code (Audit Protocol)",
      "Documented on-time delivery on the last 3 projects (client references)",
      "Friendly supplier relationships — pay invoices on time, communicate fairly",
      "Occupational safety at the highest European level",
    ],
    faClose: "When Europe looks for companies that mean something in their field, names with the Sa.Eko.Fa. seal are among the first to come to mind.",
    codeTitle: "The SauberEko® Code — 5 quiet rules",
    codeIntro: "Every certificate holder signs the Code. It is not carved in granite, but carved into the relationship with that seal:",
    codeRules: [
      ["Honesty", "If I don't know, I say \"I don't know\". If I'm wrong, I admit it. If they paid for quality, they get quality."],
      ["Cleanliness", "Clean work, clean documentation, clean workplace, clean account."],
      ["Respect", "Towards the investor, the colleague, the material, the craft."],
      ["Sharing knowledge", "A good master does not die with a secret. He teaches the next one."],
      ["Continuous learning", "As technology grows, so does the craft. We are never \"finished\"."],
    ],
    whyTitle: "Why all this?",
    whyParas: [
      "The services market is overflowing with promises. The SauberEko® certificate is the only guarantee that behind a name stands verified quality — checked by a third party, recorded in a registry, publicly traceable even ten years later.",
      "An investor who seeks a Sa.Eko.Prs. professional or a Sa.Eko.Fa. company gains insurance against unprofessionalism. A professional or company that earns the Sa.Eko. seal gains public recognition of their work and a place in a network of experts who help one another and pass on commissions.",
    ],
    whyEmphasis: ["Being certified does not mean being perfect.", "It means being responsible."],
    cta: "Join the exceptional — earn the SauberEko® certificate.",
    ctaBtn: "Get certified",
  },

  de: {
    trigger: "Über das SauberEko®-Zertifikat",
    badge: "SauberEko®-Zertifikat",
    title: "Ein Zeichen der Exzellenz",
    lead: "…und warum das goldene Siegel mehr ist als nur ein Logo.",
    originTitle: "Wie SauberEko® entstand",
    originParas: [
      "Die Marke SauberEko® entstand nicht im Büro einer Marketingagentur. Sie entstand vor Ort — bei einem Fachmann, der nach zwanzig Jahren Praxis in ganz Europa zu viele gescheiterte Projekte, gebrochene Versprechen und billige, als Professionalität verkaufte Arbeit gesehen hatte.",
    ],
    originQuote: "Mir taten die Investoren leid, die mit Millionenbudgets Qualität kauften und in Eile erledigte Arbeit erhielten — ohne Dokumentation und ohne Kontrolle.",
    originAfter: [
      "Das war der Moment. Es entstand die Idee, eine Marke zu schaffen, die etwas Echtes bedeutet. Kein leerer Aufkleber. Sondern geprüfte Qualität — von einer dritten Stelle kontrolliert, in ein Register eingetragen, über die Zeit rückverfolgbar.",
      "Sa.Eko. = SauberEko. Das deutsche Wort sauber, das griechische eko (Haus, Umfeld). Sauberkeit in jedem Detail — vom ersten Handgriff bis zur Beziehung mit dem Kunden.",
    ],
    missionTitle: "Unsere Mission",
    missionIntro: "Einen europäischen Standard aufbauen, der:",
    missionBullets: [
      "den Investor vor falschen Versprechen schützt",
      "echte Fachleute über die Masse der Unqualifizierten hebt",
      "eine Gemeinschaft aufbaut, in der Erfahrene ihr Wissen mit Neuen teilen",
      "dem Handwerk die Ehre zurückgibt, die es in der Ära „schnell, billig, Hauptsache fertig“ verloren hat",
    ],
    missionClose: "Ein Sa.Eko.-Zertifikat ist kein Papier. Es ist ein öffentliches Bekenntnis eines Menschen oder Unternehmens zu einer ethischen Arbeitsweise.",
    closeLabel: "Schließen",
    prsIcon: "user",
    prsTitle: "Zertifikat Sa.Eko.Prs. — Zertifizierte Fachleute",
    prsQuote: "Experten mit langjähriger Praxis, deren Handschrift Europa kennt.",
    prsBody: [
      "Inhaber des goldenen Sa.Eko.Prs.-Zertifikats sind Meister verschiedener Branchen mit langjähriger Erfahrung — Fachleute, Vorrichter, Rohrleger, Technologen, Qualitätsprüfer und viele weitere Experten, deren Hände und Köpfe in Dutzenden Ländern Wert schaffen — von Westeuropa bis in den Nahen Osten.",
      "Ihr Profil verbindet Praxis, Präzision und persönliche Ehre. Vor Ort sind sie ruhig, gründlich, auf Details fokussiert. Im Team sind sie einfühlsame Lehrer — sie führen Absolventen geduldig und teilen über Jahre gesammelte Erfahrung.",
    ],
    prsCondTitle: "Strenge Zertifizierungsbedingungen:",
    prsConds: [
      "Mindestens 5 Jahre nachgewiesene internationale Praxis",
      "Mindestens 3 belegte Projekte in anspruchsvollem industriellem Umfeld",
      "Direkte Qualitätskontrolle der Arbeitsergebnisse durch eine dritte Stelle",
      "Persönliche Überprüfung — Arbeitsweise, Umgang mit jüngeren Kollegen, Verschwiegenheit gegenüber dem Investor",
      "Unterschrift unter den SauberEko®-Kodex — Ehrlichkeit, Sauberkeit, Respekt",
    ],
    prsClose: "Wenn Sie in der Gemeinschaft einen Namen mit dem goldenen Sa.Eko.Prs.-Abzeichen sehen, sprechen Sie mit einem Fachmann, dem Sie blind vertrauen können.",
    faIcon: "building",
    faTitle: "Zertifikat Sa.Eko.Fa. — Zertifizierte Unternehmen",
    faQuote: "Partner, die ihr Wort halten.",
    faBody: [
      "Unternehmen mit dem Sa.Eko.Fa.-Siegel erfüllen ihre Verpflichtungen bis ins Detail — gegenüber Kunden, Mitarbeitern und Lieferanten. Sie versprechen keine unmöglichen Termine, lösen Probleme nicht durch Schuldabwälzung und drücken den Preis nicht auf Kosten der Qualität.",
      "Ihre technische und prozessuale Ausstattung ist auf höchstem Niveau. Sie investieren in Menschen, Ausbildung, Ausrüstung und Sicherheit — nicht nur in Rechnungen. Sie arbeiten mit einem gelenkten Qualitätssystem und rückverfolgbarer Dokumentation.",
    ],
    faCondTitle: "Strenge Zertifizierungsbedingungen:",
    faConds: [
      "Internes SauberEko®-Audit vor Ort — Prüfung anhand der 10 Säulen des Sa.Eko.-Kodex (Auditprotokoll)",
      "Belegte Termineinhaltung bei den letzten 3 Projekten (Kundenreferenzen)",
      "Faire Lieferantenbeziehungen — Rechnungen pünktlich bezahlen, fair kommunizieren",
      "Arbeitssicherheit auf höchstem europäischem Niveau",
    ],
    faClose: "Wenn Europa Unternehmen sucht, die in ihrem Fach etwas bedeuten, gehören Namen mit dem Sa.Eko.Fa.-Siegel zu den Ersten, an die man denkt.",
    codeTitle: "Der SauberEko®-Kodex — 5 stille Regeln",
    codeIntro: "Jeder Zertifikatsinhaber unterschreibt den Kodex. Er ist nicht in Granit gemeißelt, sondern in die Beziehung zu diesem Siegel:",
    codeRules: [
      ["Ehrlichkeit", "Wenn ich es nicht weiß, sage ich „ich weiß es nicht“. Wenn ich mich irre, gebe ich es zu. Wer für Qualität zahlt, bekommt Qualität."],
      ["Sauberkeit", "Saubere Arbeit, saubere Dokumentation, sauberer Arbeitsplatz, sauberes Konto."],
      ["Respekt", "Gegenüber dem Investor, dem Kollegen, dem Material, dem Handwerk."],
      ["Wissen teilen", "Ein guter Meister stirbt nicht mit einem Geheimnis. Er bringt es dem Nächsten bei."],
      ["Kontinuierliches Lernen", "Solange die Technik wächst, wächst auch das Handwerk. Wir sind nie „fertig“."],
    ],
    whyTitle: "Warum das alles?",
    whyParas: [
      "Der Dienstleistungsmarkt ist voller Versprechen. Das SauberEko®-Zertifikat ist die einzige Garantie, dass hinter einem Namen geprüfte Qualität steht — von einer dritten Stelle kontrolliert, in ein Register eingetragen, auch nach zehn Jahren öffentlich rückverfolgbar.",
      "Ein Investor, der einen Sa.Eko.Prs.-Fachmann oder ein Sa.Eko.Fa.-Unternehmen sucht, erhält eine Versicherung gegen Unprofessionalität. Ein Fachmann oder Unternehmen, das sich das Sa.Eko.-Siegel verdient, erhält öffentliche Anerkennung seiner Arbeit und einen Platz in einem Netzwerk von Experten, die einander helfen und Aufträge weitergeben.",
    ],
    whyEmphasis: ["Zertifiziert zu sein bedeutet nicht, perfekt zu sein.", "Es bedeutet, verantwortungsvoll zu sein."],
    cta: "Schließen Sie sich den Außergewöhnlichen an — erwerben Sie das SauberEko®-Zertifikat.",
    ctaBtn: "Zertifikat erwerben",
  },

  pl: {
    trigger: "O certyfikacie SauberEko®",
    badge: "Certyfikat SauberEko®",
    title: "Znak doskonałości",
    lead: "…i dlaczego złota pieczęć to więcej niż tylko logo.",
    originTitle: "Jak powstało SauberEko®",
    originParas: [
      "Marka SauberEko® nie narodziła się w biurze agencji marketingowej. Narodziła się w terenie — u fachowca, który po dwudziestu latach praktyki w całej Europie widział zbyt wiele nieudanych projektów, niedotrzymanych obietnic i taniej pracy podawanej za profesjonalizm.",
    ],
    originQuote: "Żal mi było inwestorów, którzy za milionowe budżety kupowali jakość, a dostawali pracę wykonaną w pośpiechu, bez dokumentacji i bez kontroli.",
    originAfter: [
      "To był ten moment. Powstał pomysł, aby zbudować markę, która będzie oznaczać coś prawdziwego. Nie pustą naklejkę. Lecz zweryfikowaną jakość — kontrolowaną przez stronę trzecią, wpisaną do rejestru, możliwą do prześledzenia w czasie.",
      "Sa.Eko. = SauberEko. Niemieckie słowo sauber (czysty), greckie eko (dom, środowisko). Czystość w każdym szczególe — od pierwszej czynności po relację z klientem.",
    ],
    missionTitle: "Nasza misja",
    missionIntro: "Zbudować europejski standard, który:",
    missionBullets: [
      "Chroni inwestora przed fałszywymi obietnicami",
      "Wyróżnia prawdziwych profesjonalistów spośród tłumu niekompetentnych",
      "Buduje społeczność, w której doświadczeni dzielą się wiedzą z nowymi",
      "Przywraca rzemiosłu honor, który utraciło w erze „szybko, tanio, byle skończyć“",
    ],
    missionClose: "Certyfikat Sa.Eko. to nie papier. To publiczne zobowiązanie człowieka lub firmy do etycznego sposobu pracy.",
    closeLabel: "Zamknij",
    prsIcon: "user",
    prsTitle: "Certyfikat Sa.Eko.Prs. — Certyfikowani profesjonaliści",
    prsQuote: "Eksperci z wieloletnią praktyką, których styl zna Europa.",
    prsBody: [
      "Posiadacze złotego certyfikatu Sa.Eko.Prs. to mistrzowie różnych branż z wieloletnim doświadczeniem — profesjonaliści, monterzy, rurarze, technolodzy, kontrolerzy jakości i wielu innych ekspertów, których ręce i umysły tworzą wartość w dziesiątkach krajów — od Europy Zachodniej po Bliski Wschód.",
      "Ich profil łączy praktykę, precyzję i osobisty honor. W terenie są cisi, dokładni, skupieni na szczególe. W zespole są empatycznymi nauczycielami — cierpliwie prowadzą absolwentów i dzielą się doświadczeniem zdobywanym latami.",
    ],
    prsCondTitle: "Rygorystyczne warunki certyfikacji:",
    prsConds: [
      "Co najmniej 5 lat zweryfikowanej praktyki międzynarodowej",
      "Co najmniej 3 udokumentowane projekty w wymagającym środowisku przemysłowym",
      "Bezpośrednia kontrola jakości wyników pracy przez stronę trzecią",
      "Osobista weryfikacja — sposób pracy, podejście do młodszych kolegów, poufność wobec inwestora",
      "Podpis pod Kodeksem SauberEko® — uczciwość, czystość, szacunek",
    ],
    prsClose: "Jeśli w społeczności widzisz nazwisko ze złotą odznaką certyfikatu Sa.Eko.Prs., rozmawiasz z ekspertem, któremu możesz ufać w ciemno.",
    faIcon: "building",
    faTitle: "Certyfikat Sa.Eko.Fa. — Certyfikowane firmy",
    faQuote: "Partnerzy, którzy dotrzymują słowa.",
    faBody: [
      "Firmy z pieczęcią Sa.Eko.Fa. to te, które co do joty wypełniają swoje zobowiązania — wobec klienta, pracowników i dostawców. Nie obiecują niemożliwych terminów, nie rozwiązują problemów przerzucaniem winy, nie zbijają ceny kosztem jakości.",
      "Ich wyposażenie techniczne i procesowe jest na najwyższym poziomie. Inwestują w ludzi, szkolenia, sprzęt i bezpieczeństwo — nie tylko w faktury. Pracują z zarządzanym systemem jakości i identyfikowalną dokumentacją.",
    ],
    faCondTitle: "Rygorystyczne warunki certyfikacji:",
    faConds: [
      "Wewnętrzny audyt SauberEko® na miejscu — weryfikacja zgodności z 10 filarami Kodeksu Sa.Eko. (Protokół audytu)",
      "Udokumentowane dotrzymanie terminów na 3 ostatnich projektach (referencje klientów)",
      "Przyjazne relacje z dostawcami — płacą faktury na czas, komunikują się uczciwie",
      "Bezpieczeństwo pracy na najwyższym europejskim poziomie",
    ],
    faClose: "Gdy w Europie szuka się firm, które coś znaczą w swojej branży, nazwy z pieczęcią Sa.Eko.Fa. są wśród pierwszych, o których się myśli.",
    codeTitle: "Kodeks SauberEko® — 5 cichych zasad",
    codeIntro: "Każdy posiadacz certyfikatu podpisuje Kodeks. Nie jest wykuty w granicie, lecz wykuty w relacji z tą pieczęcią:",
    codeRules: [
      ["Uczciwość", "Jeśli nie wiem, mówię „nie wiem“. Jeśli się mylę, przyznaję. Jeśli zapłacili za jakość, dostają jakość."],
      ["Czystość", "Czysta praca, czysta dokumentacja, czyste miejsce pracy, czyste konto."],
      ["Szacunek", "Wobec inwestora, kolegi, materiału, rzemiosła."],
      ["Dzielenie się wiedzą", "Dobry mistrz nie umiera z tajemnicą. Uczy następnego."],
      ["Ciągłe uczenie się", "Dopóki rośnie technologia, rośnie i rzemiosło. Nigdy nie jesteśmy „gotowi“."],
    ],
    whyTitle: "Po co to wszystko?",
    whyParas: [
      "Rynek usług jest przepełniony obietnicami. Certyfikat SauberEko® to jedyna gwarancja, że za nazwiskiem stoi zweryfikowana jakość — kontrolowana przez stronę trzecią, wpisana do rejestru, publicznie identyfikowalna nawet po dziesięciu latach.",
      "Inwestor, który szuka profesjonalisty Sa.Eko.Prs. lub firmy Sa.Eko.Fa., zyskuje polisę przeciw brakowi profesjonalizmu. Profesjonalista lub firma, która zasłuży na pieczęć Sa.Eko., zyskuje publiczne uznanie swojej pracy i miejsce w sieci ekspertów, którzy sobie pomagają i przekazują zlecenia.",
    ],
    whyEmphasis: ["Być certyfikowanym nie znaczy być doskonałym.", "Znaczy być odpowiedzialnym."],
    cta: "Dołącz do wyjątkowych — zdobądź certyfikat SauberEko®.",
    ctaBtn: "Zdobądź certyfikat",
  },

  fr: {
    trigger: "Qu'est-ce que le certificat SauberEko®",
    badge: "Certificat SauberEko®",
    title: "Le sceau de l'excellence",
    lead: "…et pourquoi le sceau doré est bien plus qu'un simple logo.",
    originTitle: "Comment est né SauberEko®",
    originParas: [
      "La marque SauberEko® n'est pas née dans le bureau d'une agence de marketing. Elle est née sur le terrain — chez un professionnel qui, après vingt ans de pratique à travers l'Europe, avait vu trop de projets ratés, de promesses non tenues et de travail bon marché présenté comme du professionnalisme.",
    ],
    originQuote: "J'avais de la peine pour les investisseurs qui, avec des budgets de plusieurs millions, achetaient de la qualité et recevaient un travail bâclé, sans documentation ni contrôle.",
    originAfter: [
      "Ce fut le déclic. L'idée est née de bâtir une marque qui signifierait quelque chose de réel. Pas une étiquette vide. Mais une qualité vérifiée — contrôlée par un tiers, inscrite dans un registre, traçable dans le temps.",
      "Sa.Eko. = SauberEko. Le mot allemand sauber (propre), le mot grec eko (maison, environnement). La propreté dans chaque détail — du premier geste à la relation avec le client.",
    ],
    missionTitle: "Notre mission",
    missionIntro: "Bâtir un standard européen qui :",
    missionBullets: [
      "Protège l'investisseur contre les fausses promesses",
      "Met en avant les vrais professionnels face à la masse des incompétents",
      "Construit une communauté où les expérimentés partagent leur savoir avec les nouveaux",
      "Rend au métier l'honneur qu'il a perdu à l'ère du « vite, pas cher, pourvu que ce soit fini »",
    ],
    missionClose: "Le certificat Sa.Eko. n'est pas un papier. C'est l'engagement public d'une personne ou d'une entreprise envers une manière de travailler éthique.",
    closeLabel: "Fermer",
    prsIcon: "user",
    prsTitle: "Certificat Sa.Eko.Prs. — Professionnels certifiés",
    prsQuote: "Des experts aux longues années de pratique, dont la signature est connue de toute l'Europe.",
    prsBody: [
      "Les titulaires du certificat doré Sa.Eko.Prs. sont des maîtres de divers secteurs aux longues années de pratique — professionnels, préparateurs, tuyauteurs, technologues, contrôleurs qualité et bien d'autres experts dont les mains et l'esprit créent de la valeur dans des dizaines de pays — de l'Europe occidentale au Moyen-Orient.",
      "Leur profil allie pratique, précision et honneur personnel. Sur le terrain, ils sont discrets, rigoureux, attentifs au détail. En équipe, ce sont des enseignants empathiques — ils guident patiemment les diplômés et partagent l'expérience acquise au fil des années.",
    ],
    prsCondTitle: "Conditions strictes de certification :",
    prsConds: [
      "Au moins 5 ans de pratique internationale vérifiée",
      "Au moins 3 projets documentés dans un environnement industriel exigeant",
      "Contrôle direct de la qualité des résultats du travail par un tiers",
      "Vérification personnelle — manière de travailler, approche envers les collègues plus jeunes, confidentialité envers l'investisseur",
      "Signature du Code SauberEko® — honnêteté, propreté, respect",
    ],
    prsClose: "Si tu vois dans la communauté un nom portant l'insigne doré du certificat Sa.Eko.Prs., tu parles à un expert à qui tu peux faire confiance les yeux fermés.",
    faIcon: "building",
    faTitle: "Certificat Sa.Eko.Fa. — Entreprises certifiées",
    faQuote: "Des partenaires qui tiennent parole.",
    faBody: [
      "Les entreprises portant le sceau Sa.Eko.Fa. sont celles qui honorent leurs engagements à la lettre — envers le client, les employés et les fournisseurs. Elles ne promettent pas de délais impossibles, ne règlent pas les problèmes en rejetant la faute et ne cassent pas les prix au détriment de la qualité.",
      "Leur équipement technique et leurs processus sont au plus haut niveau. Elles investissent dans les personnes, la formation, le matériel et la sécurité — pas seulement dans les factures. Elles travaillent avec un système qualité maîtrisé et une documentation traçable.",
    ],
    faCondTitle: "Conditions strictes de certification :",
    faConds: [
      "Audit interne SauberEko® directement sur site — contrôle de la conformité aux 10 piliers du Code Sa.Eko. (Protocole d'audit)",
      "Respect documenté des délais sur les 3 derniers projets (références de clients)",
      "Relations amicales avec les fournisseurs — factures payées à temps, communication loyale",
      "Sécurité au travail au plus haut niveau européen",
    ],
    faClose: "Lorsqu'en Europe on cherche des entreprises qui comptent dans leur domaine, les noms portant le sceau Sa.Eko.Fa. sont parmi les premiers auxquels on pense.",
    codeTitle: "Le Code SauberEko® — 5 règles silencieuses",
    codeIntro: "Chaque titulaire du certificat signe le Code. Il n'est pas gravé dans le granit, mais dans la relation avec ce sceau :",
    codeRules: [
      ["Honnêteté", "Si je ne sais pas, je dis « je ne sais pas ». Si je me trompe, je le reconnais. Si l'on a payé pour la qualité, on reçoit de la qualité."],
      ["Propreté", "Travail propre, documentation propre, poste de travail propre, comptes propres."],
      ["Respect", "Envers l'investisseur, le collègue, le matériau, le métier."],
      ["Partage du savoir", "Un bon maître ne meurt pas avec son secret. Il l'enseigne au suivant."],
      ["Apprentissage continu", "Tant que la technologie évolue, le métier évolue aussi. Nous ne sommes jamais « arrivés »."],
    ],
    whyTitle: "Pourquoi tout cela ?",
    whyParas: [
      "Le marché des services déborde de promesses. Le certificat SauberEko® est la seule garantie qu'un nom repose sur une qualité vérifiée — contrôlée par un tiers, inscrite dans un registre, consultable publiquement même dix ans plus tard.",
      "L'investisseur qui recherche un expert Sa.Eko.Prs. ou une entreprise Sa.Eko.Fa. obtient une assurance contre le manque de professionnalisme. Le professionnel ou l'entreprise qui mérite le sceau Sa.Eko. obtient la reconnaissance publique de son travail et son intégration dans un réseau d'experts qui s'entraident et se transmettent des commandes.",
    ],
    whyEmphasis: ["Être certifié ne signifie pas être parfait.", "Cela signifie être responsable."],
    cta: "Rejoignez les meilleurs — obtenez le certificat SauberEko®.",
    ctaBtn: "Obtenir le certificat",
  },
};

const overlay = { hidden: { opacity: 0 }, show: { opacity: 1 }, exit: { opacity: 0 } };
const panel = {
  hidden: { opacity: 0, y: 28, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: 20, scale: 0.98, transition: { duration: 0.22 } },
};

export const CertificateTrigger = ({ compact = false, onClick }) => {
  const { lang } = useLang();
  const c = CERT_COPY[lang] || CERT_COPY.sk;
  if (compact) {
    return (
      <button data-testid="cert-showcase-trigger" onClick={onClick}
        className="flex items-center gap-2 text-left font-display text-2xl text-[#D4AF37] hover:text-[#F2E3C6] transition-colors">
        <Sparkles size={22} /> {c.trigger}
      </button>
    );
  }
  return (
    <button data-testid="cert-showcase-trigger" onClick={onClick}
      className="group inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-1.5 text-xs tracking-wide text-[#D4AF37] hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/70 transition-colors duration-300">
      <Sparkles size={14} className="group-hover:rotate-12 transition-transform duration-300" />
      <span className="whitespace-nowrap">{c.trigger}</span>
    </button>
  );
};

const H = ({ children }) => <h3 className="font-display text-2xl sm:text-[26px] text-white mb-4">{children}</h3>;
const P = ({ children }) => <p className="text-[15px] leading-relaxed text-white/65 mb-4">{children}</p>;

const CheckList = ({ title, items }) => (
  <div className="mt-4">
    {title && <p className="text-[13px] uppercase tracking-wider text-[#D4AF37]/80 mb-3">{title}</p>}
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center bg-[#D4AF37] text-black"><Check size={13} strokeWidth={3} /></span>
          <span className="text-[14.5px] leading-relaxed text-white/70">{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CertCard = ({ icon, title, quote, body, condTitle, conds, close }) => (
  <div className="my-8 border border-[#D4AF37]/25 bg-gradient-to-b from-[#D4AF37]/[0.07] to-transparent p-6 sm:p-8">
    <div className="flex items-center gap-3 mb-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]">
        {icon === "building" ? <Building2 size={20} strokeWidth={1.5} /> : <User size={20} strokeWidth={1.5} />}
      </span>
      <h3 className="font-display text-xl sm:text-2xl text-white leading-tight">{title}</h3>
    </div>
    <p className="mb-4 flex items-start gap-2 text-[15px] italic text-[#D4AF37]/90">
      <Quote size={15} className="mt-1 shrink-0 opacity-70" /> {quote}
    </p>
    {body.map((p, i) => <P key={i}>{p}</P>)}
    <CheckList title={condTitle} items={conds} />
    <p className="mt-5 text-[14.5px] leading-relaxed text-white/75 border-l-2 border-[#D4AF37]/60 pl-4">{close}</p>
  </div>
);

export const CertificateModal = ({ open, onClose }) => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const c = CERT_COPY[lang] || CERT_COPY.sk;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  const getCertified = () => { onClose(); navigate("/ziadost"); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="cert-showcase-overlay" variants={overlay} initial="hidden" animate="show" exit="exit"
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-md p-3 sm:p-6">
          <motion.div data-testid="cert-showcase-modal" role="dialog" aria-modal="true" variants={panel}
            onClick={(e) => e.stopPropagation()}
            className="relative my-6 w-full max-w-3xl overflow-hidden border border-[#D4AF37]/30 bg-gradient-to-b from-[#0d0d0c] to-[#070707] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]">
            <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-20 blur-3xl"
              style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }} />

            <button data-testid="cert-showcase-close" onClick={onClose} aria-label="Zavrieť"
              className="absolute right-4 top-4 z-20 flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-black/60 backdrop-blur px-3 py-1.5 text-white/80 hover:text-black hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-colors"><X size={18} /><span className="text-xs font-medium">{c.closeLabel || "Zavrieť"}</span></button>

            <div className="relative px-6 sm:px-12 py-10 sm:py-12">
              <CertificateBody onCta={getCertified} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const CertificateBody = ({ onCta }) => {
  const { lang } = useLang();
  const c = CERT_COPY[lang] || CERT_COPY.sk;
  return (
    <>
              {/* Header */}
              <div className="flex flex-col items-center text-center mb-10">
                <div className="relative mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                  <Award size={34} className="text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="absolute inset-0 rounded-full ring-1 ring-[#D4AF37]/20 animate-pulse" />
                </div>
                <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37]/80">{c.badge}</span>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-tight text-white">
                  {c.title.split(" ").slice(0, -1).join(" ")} <span className="gold-text">{c.title.split(" ").slice(-1)}</span>
                </h2>
                <p className="mt-4 max-w-xl text-[15px] italic text-white/50">{c.lead}</p>
              </div>

              <div className="mx-auto max-w-2xl">
                {/* Origin */}
                <H>{c.originTitle}</H>
                {c.originParas.map((p, i) => <P key={i}>{p}</P>)}
                <blockquote className="my-5 border-l-2 border-[#D4AF37] bg-[#D4AF37]/[0.06] px-6 py-4 text-[15px] italic leading-relaxed text-white/80">
                  {c.originQuote}
                </blockquote>
                {c.originAfter.map((p, i) => <P key={i}>{p}</P>)}

                <div className="my-8 flex items-center justify-center gap-3">
                  <span className="h-px w-12 bg-[#D4AF37]/40" /><Leaf size={18} className="text-[#D4AF37]" strokeWidth={1.5} /><span className="h-px w-12 bg-[#D4AF37]/40" />
                </div>

                {/* Mission */}
                <H>{c.missionTitle}</H>
                <P>{c.missionIntro}</P>
                <CheckList items={c.missionBullets} />
                <p className="mt-5 font-display text-lg leading-snug text-white/90">{c.missionClose}</p>

                {/* Certificate types */}
                <CertCard icon={c.prsIcon} title={c.prsTitle} quote={c.prsQuote} body={c.prsBody} condTitle={c.prsCondTitle} conds={c.prsConds} close={c.prsClose} />
                <CertCard icon={c.faIcon} title={c.faTitle} quote={c.faQuote} body={c.faBody} condTitle={c.faCondTitle} conds={c.faConds} close={c.faClose} />

                {/* Code */}
                <H>{c.codeTitle}</H>
                <P>{c.codeIntro}</P>
                <div className="space-y-3">
                  {c.codeRules.map(([name, text], i) => (
                    <div key={i} className="flex items-start gap-4 border border-white/10 bg-white/[0.02] p-4">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] font-display text-lg text-black">{i + 1}</span>
                      <div>
                        <p className="font-semibold text-[#D4AF37]">{name}</p>
                        <p className="text-[14px] leading-relaxed text-white/65 mt-0.5">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Why */}
                <div className="my-8 flex items-center justify-center gap-3">
                  <span className="h-px w-12 bg-[#D4AF37]/40" /><Leaf size={18} className="text-[#D4AF37]" strokeWidth={1.5} /><span className="h-px w-12 bg-[#D4AF37]/40" />
                </div>
                <H>{c.whyTitle}</H>
                {c.whyParas.map((p, i) => <P key={i}>{p}</P>)}
                <div className="mt-6 text-center">
                  <p className="font-display text-2xl text-white">{c.whyEmphasis[0]}</p>
                  <p className="font-display text-2xl gold-text">{c.whyEmphasis[1]}</p>
                </div>

                {/* CTA */}
                <div className="mt-10 flex flex-col items-center text-center">
                  <p className="mb-6 max-w-lg font-display text-lg leading-snug text-white/90">{c.cta}</p>
                  <button data-testid="cert-showcase-cta" onClick={onCta}
                    className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] px-9 py-3.5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#F2E3C6]">
                    <ShieldCheck size={17} /> {c.ctaBtn}
                    <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
    </>
  );
};
